/**
 * Excel template of the bulk product creation, built and read with exceljs like the bulk asset
 * template of web.ccp-assets.
 *
 * - The template belongs to one production project: its columns are the ones the sheet shows
 *   for that project, and the project id travels in a hidden sheet so the import can restore it.
 * - Selector columns get a dropdown whose values live in a hidden sheet, so whoever fills the
 *   template can't delete them by accident. The dropdown is a help, not a guarantee (pasted
 *   values skip it), so the import resolves and validates every value again.
 * - Columns are read by the field keys stored in the hidden sheet, not by their headers: the
 *   headers are translated and the columns depend on the project.
 */

/** Hidden sheet with the project id (A1) and the field key of each column (row 2) */
const META_SHEET = 'ccp'

export interface TemplateColumn {
  key: string;
  header: string;
  width: number;
  // Values of the dropdown, none for free text columns
  options?: string[];
  // Free text columns kept as text so Excel doesn't turn codes like 012345 into numbers
  text?: boolean;
  date?: boolean;
  // Lists that accept several comma-separated values can't reject what is not in the list
  multiple?: boolean;
}

export interface TemplateTexts {
  sheet: string;
  lists: string;
}

export interface TemplateContent {
  projectId: string | null;
  rows: { [key: string]: string }[];
}

/**
 * Loads exceljs on demand: it weighs about a megabyte and is only needed when someone
 * downloads or imports a template. It is chosen by what the module has, not by its shape,
 * because the bundler may expose it directly or under `default`.
 */
async function loadExcelJS(): Promise<typeof import('exceljs')> {
  const mod = await import(/* webpackChunkName: "exceljs" */ 'exceljs')
  const candidate = (mod as unknown as { default?: unknown }).default
  const hasWorkbook = (value: unknown) =>
    !!value && typeof (value as { Workbook?: unknown }).Workbook === 'function'
  if (hasWorkbook(mod)) return mod
  if (hasWorkbook(candidate)) return candidate as typeof import('exceljs')
  throw new Error('exceljs loaded without Workbook')
}

const quoteSheet = (name: string) => `'${name.replace(/'/g, "''")}'`

export async function buildProductTemplate(
  projectId: string,
  columns: TemplateColumn[],
  texts: TemplateTexts,
  blankRows = 200
): Promise<Blob> {
  const ExcelJS = await loadExcelJS()
  const workbook = new ExcelJS.Workbook()

  const sheet = workbook.addWorksheet(texts.sheet, { views: [{ state: 'frozen', ySplit: 1 }] })
  sheet.columns = columns.map(column => ({ header: column.header, key: column.key, width: column.width }))
  sheet.getRow(1).font = { bold: true }

  const lists = workbook.addWorksheet(texts.lists)
  lists.state = 'veryHidden'
  const meta = workbook.addWorksheet(META_SHEET)
  meta.state = 'veryHidden'
  meta.getCell(1, 1).value = projectId
  columns.forEach((column, index) => { meta.getCell(2, index + 1).value = column.key })

  let listColumn = 0
  columns.forEach((column, index) => {
    const sheetColumn = sheet.getColumn(index + 1)
    if (column.text) sheetColumn.numFmt = '@'
    if (column.date) sheetColumn.numFmt = 'yyyy-mm-dd'
    // An empty list gives an inverted range and Excel leaves a dead dropdown: no validation then
    if (!column.options || !column.options.length) return

    listColumn++
    const listSheetColumn = lists.getColumn(listColumn)
    listSheetColumn.values = [column.header, ...column.options]
    const range = `${quoteSheet(texts.lists)}!$${listSheetColumn.letter}$2:$${listSheetColumn.letter}$${column.options.length + 1}`
    for (let row = 2; row <= blankRows + 1; row++) {
      sheet.getCell(row, index + 1).dataValidation = {
        type: 'list',
        allowBlank: true,
        showErrorMessage: !column.multiple,
        formulae: [range]
      }
    }
  })

  const buffer = await workbook.xlsx.writeBuffer()
  return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
}

const pad = (value: number) => (value < 10 ? '0' : '') + value

/** Cell value as text; dates as 'YYYY-MM-DD' (exceljs gives them as UTC dates) */
function cellText(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (value instanceof Date) {
    return `${value.getUTCFullYear()}-${pad(value.getUTCMonth() + 1)}-${pad(value.getUTCDate())}`
  }
  if (typeof value === 'object') {
    const object = value as { text?: unknown; result?: unknown; richText?: { text: string }[] }
    if (object.richText) return object.richText.map(part => part.text).join('').trim()
    if (object.text !== undefined) return cellText(object.text)
    if (object.result !== undefined) return cellText(object.result)
    return ''
  }
  return String(value).trim()
}

/**
 * Reads a template: the project it was made for and its non-empty rows, keyed by field.
 * Throws when the file is not a template of this form (no hidden sheet with the field keys).
 */
export async function readProductTemplate(file: File): Promise<TemplateContent> {
  const ExcelJS = await loadExcelJS()
  const workbook = new ExcelJS.Workbook()
  await workbook.xlsx.load(await file.arrayBuffer())

  const meta = workbook.getWorksheet(META_SHEET)
  const sheet = workbook.worksheets.find(item => item.state !== 'veryHidden' && item.state !== 'hidden')
  if (!meta || !sheet) throw new Error('Not a bulk product template')

  const keys: string[] = []
  meta.getRow(2).eachCell((cell, column) => { keys[column - 1] = cellText(cell.value) })

  const rows: { [key: string]: string }[] = []
  sheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return
    const values: { [key: string]: string } = {}
    keys.forEach((key, index) => { if (key) values[key] = cellText(row.getCell(index + 1).value) })
    // Blank rows are the padding of the template, not data
    if (Object.keys(values).some(key => values[key])) rows.push(values)
  })
  return { projectId: cellText(meta.getCell(1, 1).value) || null, rows }
}

export function downloadBlob(blob: Blob, fileName: string) {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}
